import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../Context/Languagecontext";
import "../Style/Navbar.css";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t, toggleLanguage } = useLanguage();
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to hash after navigation
  const smoothScrollTo = (targetY, duration = 800) => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    let startTime = null;

    const animation = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const time = currentTime - startTime;

      const progress = Math.min(time / duration, 1);

      // ease-in-out
      const ease =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      window.scrollTo(0, startY + distance * ease);

      if (time < duration) requestAnimationFrame(animation);
    };

    requestAnimationFrame(animation);
  };
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);

      if (el) {
        const yOffset = -80;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

        smoothScrollTo(y, 800);
      }
    }
  }, [location]);
  const dropdownMap = {
    about: [
      { label: t.nav.dropdowns.about[0], hash: "journey" },
      { label: t.nav.dropdowns.about[1], hash: "cg" },
      { label: t.nav.dropdowns.about[2], hash: "earlylife" },
      { label: t.nav.dropdowns.about[3], hash: "milestone" },
    ],
    business: [
      { label: t.nav.dropdowns.business[0], hash: "BStory" },
      { label: t.nav.dropdowns.business[1], hash: "BPortfolio" },
      { label: t.nav.dropdowns.business[2], hash: "ManufacturingExcellence" },
      { label: t.nav.dropdowns.business[3], hash: "ProductsIndustries" },
      { label: t.nav.dropdowns.business[4], hash: "ImpactStats" },
      { label: t.nav.dropdowns.business[5], hash: "MilestonesTimeline" },
    ],
    // political: [
    //   { label: t.nav.dropdowns.political[0], hash: "Principles" },
    //   { label: t.nav.dropdowns.political[1], hash: "EducationalLegacy" },
    //   { label: t.nav.dropdowns.political[2], hash: "CommunityWelfare" },
    //   { label: t.nav.dropdowns.political[3], hash: "PublicLeadership" },
    //   { label: t.nav.dropdowns.political[4], hash: "CulturalSection" },
    //   { label: t.nav.dropdowns.political[5], hash: "Temple" },
    //   { label: t.nav.dropdowns.political[6], hash: "Awards" },
    //   { label: t.nav.dropdowns.political[7], hash: "Milestones" },
    //   { label: t.nav.dropdowns.political[8], hash: "Impact" },
    // ],
  };

  const navLinks = [
    { label: t.nav.home, href: "/" },
    { label: t.nav.about, href: "/about", dropdownKey: "about" },
    { label: t.nav.business, href: "/business", dropdownKey: "business" },
    // { label: t.nav.political, href: "/political", dropdownKey: "political" },
    { label: t.nav.gallery, href: "/gallery" },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const handleDropdownClick = (pageHref, hash) => {
    setActiveDropdown(null);
    setMenuOpen(false);
    navigate(`${pageHref}#${hash}`);
  };

  const renderDropdownItems = (link) => {
    if (!link.dropdownKey) return null;
    const items = dropdownMap[link.dropdownKey];
    return items.map((item) => (
      <button
        key={item.hash}
        className="navbar__dropdown-item"
        onClick={() => handleDropdownClick(link.href, item.hash)}
      >
        {item.label}
      </button>
    ));
  };

  const renderNavItem = (link) => (
    <li
      key={link.label}
      className="navbar__item"
      onMouseEnter={() => link.dropdownKey && setActiveDropdown(link.label)}
      onMouseLeave={() => setActiveDropdown(null)}
    >
      <Link
        to={link.href}
        className={`navbar__link ${isActive(link.href) ? "navbar__link--active" : ""}`}
      >
        {link.label}
        {link.dropdownKey && (
          <span className="navbar__chevron">
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
      </Link>

      {link.dropdownKey && activeDropdown === link.label && (
        <div className="navbar__dropdown">{renderDropdownItems(link)}</div>
      )}
    </li>
  );

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${lang === 'ta' ? 'navbar--tamil' : ''}`}>
      <div className="navbar__inner">
        {/* Centered group: logo, then links, then language switcher */}
        <div className="navbar__center">
          <Link to="/" className="navbar__logo">
            <span className="navbar__logo-text">KKB</span>
            <span className="navbar__logo-sub">{t.hero.tagline}</span>
          </Link>

          <ul className="navbar__links">
            {navLinks.map(renderNavItem)}
          </ul>

          <button
            className="navbar__lang-toggle"
            onClick={toggleLanguage}
            aria-label="Toggle language"
            title={lang === "en" ? "Switch to Tamil" : "Switch to English"}
          >
            <span
              className={`navbar__lang-option ${lang === "en" ? "navbar__lang-option--active" : ""}`}
            >
              EN
            </span>
            <span className="navbar__lang-divider">|</span>
            <span
              className={`navbar__lang-option ${lang === "ta" ? "navbar__lang-option--active" : ""}`}
            >
              த
            </span>
          </button>
        </div>

        {/* MOBILE HAMBURGER */}
        <button
          className={`navbar__hamburger ${menuOpen ? "navbar__hamburger--open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`navbar__mobile ${menuOpen ? "navbar__mobile--open" : ""}`}
      >
        {navLinks.map((link) => (
          <React.Fragment key={link.label}>
            <Link
              to={link.href}
              className={`navbar__mobile-link ${isActive(link.href) ? "navbar__mobile-link--active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
            {link.dropdownKey &&
              dropdownMap[link.dropdownKey].map((item) => (
                <button
                  key={item.hash}
                  className="navbar__mobile-link navbar__mobile-link--sub"
                  onClick={() => handleDropdownClick(link.href, item.hash)}
                >
                  {item.label}
                </button>
              ))}
          </React.Fragment>
        ))}

        <button
          className="navbar__mobile-lang"
          onClick={toggleLanguage}
          aria-label="Toggle language"
        >
          <span className={lang === "en" ? "navbar__mobile-lang--active" : ""}>
            English
          </span>
          <span className="navbar__lang-divider"> | </span>
          <span className={lang === "ta" ? "navbar__mobile-lang--active" : ""}>
            தமிழ்
          </span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;